import { useEffect, useMemo } from "react";

import { isEqual } from "lodash";

import { useMMMDateRange } from "@features/mmm/hooks/mmm-url-state";
import { useDesignElementManager } from "@hooks/use-design-element-manager";
import { usePrevious } from "@hooks/use-previous";
import {
  safeDivide,
  type BudgetMixBreakdownSpendDatum,
} from "@north-beam/nb-common";

import { useHistoricalBudgetMix } from "../budget-mix-card/use-historical-budget-mix";

import {
  type BudgetMixChartDatum,
  SegmentedBudgetMixChart,
} from "./segmented-chart";
import { breakdownDataBy } from "./utils";

export const BudgetMixChartContainer = ({
  chartData,
  isHistorical,
}: {
  chartData: BudgetMixBreakdownSpendDatum[];
  isHistorical: boolean;
}) => {
  const { setHistoricalBudgetMixData } = useHistoricalBudgetMix();
  const breakdownIdentifier = chartData[0]?.breakdowns[0]?.key;
  const { designElementManager } = useDesignElementManager();
  const [dateRange] = useMMMDateRange();

  const { budgetMixChartData } = useMemo(() => {
    const {
      budgetMixChartData: budgetMixChartDataLocal,
      totalSpendForGroup: totalSpendForGroupLocal,
      totalSpend: totalSpendLocal,
    } = breakdownDataBy(chartData, designElementManager, breakdownIdentifier);

    const spendRatio = safeDivide(totalSpendForGroupLocal, totalSpendLocal, {
      zeroOverZero: 0,
      infOverInf: 1,
    });

    return {
      budgetMixChartData: budgetMixChartDataLocal.map(
        (datum: BudgetMixChartDatum) => ({
          ...datum,
          percentage: datum.percentage * spendRatio,
        }),
      ),
    };
  }, [breakdownIdentifier, designElementManager, chartData]);

  const prevHistoricalBudgetMixData = usePrevious(budgetMixChartData);
  useEffect(() => {
    const filterFieldsForComparison = (data?: BudgetMixChartDatum[]) =>
      data?.map((datum) => ({
        breakdownKey: datum?.breakdownKey,
        breakdownValue: datum?.breakdownValue,
        spend: datum?.spend,
        percentage: datum?.percentage,
      }));

    if (
      isHistorical &&
      !isEqual(
        filterFieldsForComparison(budgetMixChartData),
        filterFieldsForComparison(prevHistoricalBudgetMixData),
      )
    ) {
      setHistoricalBudgetMixData(budgetMixChartData);
    }
  }, [
    isHistorical,
    prevHistoricalBudgetMixData,
    budgetMixChartData,
    setHistoricalBudgetMixData,
  ]);

  return (
    <>
      <div className="w-full flex justify-between px-3">
        <div className="w-full flex flex-col">
          <span className="text-xs font-semibold">BREAKDOWN</span>
          <span className="text-xxs font-normal">{breakdownIdentifier}</span>
        </div>
      </div>
      <SegmentedBudgetMixChart
        chartData={budgetMixChartData}
        isHistorical={isHistorical}
        dateRange={dateRange}
      />
    </>
  );
};
